import type { SongFormSchemaData } from "./songFormSchema";

// Type for writer selection with CAE/IPI number
type WriterForSelection = {
  writercode: string;
  caeipinumber?: string | null;
  dateofbirth?: string | null;
  writerexternalregistrationstatus?: string | null;
};

// Type for site configuration
type SiteConfigData = {
  ipicae_required?: boolean;
  date_of_birth_required?: boolean;
};

// Type for business validation result
export type BusinessValidationResult = {
  isValid: boolean;
  issues: string[];
  hasWriters: boolean;
  sharesEqual100: boolean;
  hasRightToCollectWriter: boolean;
  hasValidCAEIPI: boolean;
};

/**
 * Validates business logic for song creation that cannot be expressed in Zod schemas
 * Includes complex cross-field validations and external dependency checks
 */
export function validateSongBusinessLogic(
  formValues: Partial<SongFormSchemaData>,
  siteConfigData: SiteConfigData | undefined,
  selectedWriters: WriterForSelection[],
  t: (key: string) => string | null,
  isTunecore: boolean = false,
): BusinessValidationResult {
  const formWriters = formValues.writers ?? [];

  // Business logic validations only - form-level validation is handled by Zod
  const hasWriters = formWriters.length > 0;
  const totalShares = formWriters.reduce((sum: number, writer: { writershare?: number }) => sum + (writer.writershare ?? 0), 0);
  const sharesEqual100 = Math.abs(totalShares - 100) < 0.01;
  const hasRightToCollectWriter = formWriters.some((writer: { righttocollect?: boolean }) => writer.righttocollect === true);

  // CAE/IPI validation based on external site configuration
  // Only validate DOB and CAE/IPI for Tunecore users
  const needsCAEIPI = siteConfigData?.ipicae_required === true && isTunecore;
  const hasValidCAEIPI = !needsCAEIPI || formWriters.every((writer: { righttocollect?: boolean; writercode: string }) => {
    if (writer.righttocollect) {
      const selectedWriter = selectedWriters.find(sw => sw.writercode === writer.writercode);
      if (!selectedWriter)
        return false;

      // Check if writer has IMRO registration pending (writerexternalregistrationstatus not null)
      const hasIMRORegistrationPending = Boolean(selectedWriter.writerexternalregistrationstatus != null);

      // Validation requirements differ based on IMRO registration status:
      if (hasIMRORegistrationPending) {
        // If IMRO registration is pending, only DOB is required
        const hasDateOfBirth = selectedWriter.dateofbirth != null && selectedWriter.dateofbirth.trim() !== "";
        return hasDateOfBirth;
      }
      else {
        // If no IMRO registration pending, both DOB and CAE/IPI are required
        const hasDateOfBirth = selectedWriter.dateofbirth != null && selectedWriter.dateofbirth.trim() !== "";
        const hasCAEIPI = selectedWriter.caeipinumber != null && selectedWriter.caeipinumber.trim() !== "";
        return hasDateOfBirth && hasCAEIPI;
      }
    }
    return true;
  });

  const issues: string[] = [];
  // Only include complex business logic validations that Zod cannot handle
  // Form-level validations (title, performing artists, etc.) are handled by Zod schema
  if (!hasWriters)
    issues.push(t("songs:at-least-one-writer-required") ?? "At least one writer is required");
  if (hasWriters && !sharesEqual100)
    issues.push(t("songs:total-writer-shares-must-equal-100") ?? "Total writer shares must equal 100%");
  if (hasWriters && !hasRightToCollectWriter)
    issues.push(t("songs:at-least-one-writer-rtc") ?? "At least one writer must have 'Right To Collect' enabled");
  if (!hasValidCAEIPI)
    issues.push(t("songs:writers-rtc-must-have-dob-cae-ipi") ?? "Writers with 'Right To Collect' must have Date of Birth and CAE/IPI numbers (or just Date of Birth if IMRO registration is pending)");

  return {
    isValid: issues.length === 0,
    issues,
    hasWriters,
    sharesEqual100,
    hasRightToCollectWriter,
    hasValidCAEIPI,
  };
}
